import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { emailTrackingAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Mail,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Send,
  AlertCircle,
  ChevronRight,
  Filter
} from "lucide-react";
import { 
  EmailStatusBadge, 
  EmailTrackingCard, 
  EmailDeliveryStats,
  EmailDeliveryAlert
} from "../components/EmailTracking";

// Status filter options
const STATUS_OPTIONS = [
  { value: "all", label: "Alle Status" },
  { value: "pending", label: "Ausstehend" },
  { value: "sent", label: "Übergeben" },
  { value: "delivered", label: "Zugestellt" },
  { value: "delivery_delayed", label: "Verzögert" },
  { value: "bounced", label: "Nicht zustellbar" },
  { value: "failed", label: "Fehlgeschlagen" },
  { value: "complained", label: "Spam" }
];

// Entity type labels
const ENTITY_TYPE_LABELS = {
  invoice: "Rechnung",
  free_invoice: "Freitext-Rechnung",
  offer: "Angebot",
  free_offer: "Freitext-Angebot",
  contract: "Kaufvertrag",
  appointment: "Termin",
  signing_request: "Signatur-Anfrage",
  signing_otp: "OTP-Code",
  signing_confirmation: "Signatur-Bestätigung"
};

// Format date helper
const formatDateTime = (isoString) => {
  if (!isoString) return "-";
  try {
    const date = new Date(isoString);
    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return isoString;
  }
};

export default function EmailTrackingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      
      const [emailsRes, statsRes] = await Promise.all([
        emailTrackingAPI.list(params),
        emailTrackingAPI.getStats(30)
      ]);
      
      setEmails(emailsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to load email tracking data:", error);
      toast.error("Fehler beim Laden der E-Mail-Daten");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Filter emails by search query
  const filteredEmails = emails.filter(email => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      email.to_email?.toLowerCase().includes(query) ||
      email.subject?.toLowerCase().includes(query) ||
      email.entity_id?.toLowerCase().includes(query)
    );
  });
  
  // Open detail dialog
  const handleViewDetail = async (email) => {
    try {
      const response = await emailTrackingAPI.get(email.id);
      setSelectedEmail(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error("Failed to load email details:", error);
      toast.error("Fehler beim Laden der E-Mail-Details");
    }
  };
  
  // Get status counts
  const getStatusCount = (status) => {
    return emails.filter(e => e.send_status === status).length;
  };
  
  return (
    <div className="p-6 space-y-6" data-testid="email-tracking-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">E-Mail Zustellstatus</h1>
          <p className="text-muted-foreground">
            Überwachen Sie den Zustellstatus aller versendeten E-Mails
          </p>
        </div>
        <Button onClick={loadData} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Mail className="h-6 w-6 mx-auto mb-2 text-gray-500" />
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Gesamt</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-6 w-6 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold text-green-600">{stats.delivered}</p>
              <p className="text-sm text-muted-foreground">Zugestellt</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Send className="h-6 w-6 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold text-blue-600">{stats.sent}</p>
              <p className="text-sm text-muted-foreground">Übergeben</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="h-6 w-6 mx-auto mb-2 text-yellow-500" />
              <p className="text-2xl font-bold text-yellow-600">{stats.pending + stats.delivery_delayed}</p>
              <p className="text-sm text-muted-foreground">Ausstehend</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <XCircle className="h-6 w-6 mx-auto mb-2 text-red-500" />
              <p className="text-2xl font-bold text-red-600">{stats.bounced + stats.failed}</p>
              <p className="text-sm text-muted-foreground">Fehlgeschlagen</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold mb-1">
                <span className={
                  stats.delivery_rate >= 95 ? "text-green-600" :
                  stats.delivery_rate >= 80 ? "text-yellow-600" : "text-red-600"
                }>
                  {stats.delivery_rate}%
                </span>
              </div>
              <p className="text-sm text-muted-foreground">Zustellrate</p>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Suchen nach E-Mail, Betreff..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-full md:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Failed Emails Alert */}
      {stats && (stats.bounced > 0 || stats.failed > 0) && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-800">
                  {stats.bounced + stats.failed} E-Mail(s) nicht zugestellt
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  Einige E-Mails konnten nicht zugestellt werden. Bitte prüfen Sie die E-Mail-Adressen.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setStatusFilter("bounced")}
                >
                  Fehlerhafte E-Mails anzeigen
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Email Table */}
      <Card>
        <CardHeader>
          <CardTitle>E-Mail-Verlauf</CardTitle>
          <CardDescription>
            {filteredEmails.length} E-Mail(s) gefunden
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Keine E-Mails gefunden</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Empfänger</TableHead>
                  <TableHead>Betreff</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmails.map((email) => (
                  <TableRow 
                    key={email.id}
                    className={
                      email.send_status === "bounced" || email.send_status === "failed"
                        ? "bg-red-50"
                        : email.send_status === "delivered"
                        ? "bg-green-50/50"
                        : ""
                    }
                  >
                    <TableCell>
                      <EmailStatusBadge status={email.send_status} />
                    </TableCell>
                    <TableCell className="font-medium">
                      {email.to_email}
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {email.subject}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {ENTITY_TYPE_LABELS[email.entity_type] || email.entity_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDateTime(email.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleViewDetail(email)}
                      >
                        Details
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>E-Mail Details</DialogTitle>
          </DialogHeader>
          
          {selectedEmail && (
            <div className="space-y-4">
              {/* Alert for failed emails */}
              {(selectedEmail.send_status === "bounced" || 
                selectedEmail.send_status === "failed" ||
                selectedEmail.send_status === "complained") && (
                <EmailDeliveryAlert email={selectedEmail} />
              )}
              
              {/* Email card */}
              <EmailTrackingCard email={selectedEmail} showDetails={true} />
              
              {/* Event History */}
              {selectedEmail.events && selectedEmail.events.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium">Ereignis-Verlauf</h4>
                  <div className="space-y-2">
                    {selectedEmail.events.map((event, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center gap-3 p-2 bg-gray-50 rounded text-sm"
                      >
                        <div className={`w-2 h-2 rounded-full ${
                          event.event_type.includes("delivered") ? "bg-green-500" :
                          event.event_type.includes("bounced") || event.event_type.includes("failed") ? "bg-red-500" :
                          "bg-blue-500"
                        }`} />
                        <span className="font-mono text-xs text-muted-foreground">
                          {formatDateTime(event.received_at)}
                        </span>
                        <span>{event.event_type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
